import { UnableToDispenseReasonLabel } from '@/components/order/order.types';
import { useErrorManagement } from '@/context/ErrorManagement';
import type { ListOrdersResponse } from '@/context/orders/DeliveryTableFilteredData';
import { PharmacistOrdersContext } from '@/context/orders/PharmacistOrders';
import { confirmDispenseOrder, getUnableToDispenseInfo, unableToDispense } from '@/services/pharmacist.service';
import { Logger } from '@/utils/logger';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import { useClearSelectedOrders } from '@montugroup/circuit-shared/context/table/OrderSelectionContext';
import type {
  PharmacistOrder,
  UnableToDispenseInfo,
  UnableToDispenseProduct,
} from '@montugroup/circuit-shared/types/index';
import { noOp } from '@montugroup/circuit-shared/utils/noOp';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { toast } from '@montugroup/design-system';
import { useQueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ApprovalDocumentsModalState = {
  visible: boolean;
  patientCode: string | null;
  orderId: number | null;
};

type UnableToDispenseModalState = {
  visible: boolean;
  data: UnableToDispenseInfo | null;
};

type ConfirmDispenseModalState = {
  visible: boolean;
  orderIds?: number[];
  data?: number;
  refetchOrdersFunction?: () => void;
};

type MarkAsCollectedModalState = {
  visible: boolean;
  orderIds?: number[];
};

export type SplitOrderModalOrderData = {
  orderId: number;
  orderCode: string;
  shippedDate?: string;
  patientCode: string;
  prescriptionNames: string[];
  productDetails: {
    name: string;
    id: number | string;
    quantity: number | undefined;
  }[];
};

type SplitOrderModalState = {
  visible: boolean;
  orderDetails: SplitOrderModalOrderData | null;
};

type SaveUnableToDispenseResult = {
  success: boolean;
  message?: string;
};

const logger = new Logger('PharmacistOrdersModalsContext');

export type PharmacistOrdersModalsData = {
  approvalDocumentsModal: ApprovalDocumentsModalState;
  setApprovalDocumentsModal: (approvalDocumentsModal: ApprovalDocumentsModalState) => void;
  unableToDispenseModal: UnableToDispenseModalState;
  setUnableToDispenseModal: (unableToDispenseModal: UnableToDispenseModalState) => void;
  confirmDispenseModal: ConfirmDispenseModalState;
  setConfirmDispenseModal: (confirmDispenseModal: ConfirmDispenseModalState) => void;
  markAsCollectedModal: MarkAsCollectedModalState;
  setMarkAsCollectedModal: (markAsCollectedModal: MarkAsCollectedModalState) => void;
  splitOrderModal: SplitOrderModalState;
  setSplitOrderModal: (splitOrderModal: SplitOrderModalState) => void;
};

export const PharmacistOrdersModalContext = createContext<PharmacistOrdersModalsData>({
  approvalDocumentsModal: { visible: false, patientCode: null, orderId: null },
  setApprovalDocumentsModal: noOp,
  unableToDispenseModal: {
    visible: false,
    data: null,
  },
  setUnableToDispenseModal: noOp,
  confirmDispenseModal: { visible: false, orderIds: [], refetchOrdersFunction: undefined },
  setConfirmDispenseModal: noOp,
  markAsCollectedModal: { visible: false, orderIds: [] },
  setMarkAsCollectedModal: noOp,
  splitOrderModal: { visible: false, orderDetails: null },
  setSplitOrderModal: noOp,
});

export function PharmacistOrdersModalsProvider(props: PropsWithChildren) {
  const { children } = props;
  const [approvalDocumentsModal, setApprovalDocumentsModal] = useState<ApprovalDocumentsModalState>({
    visible: false,
    patientCode: null,
    orderId: null,
  });
  const [unableToDispenseModal, setUnableToDispenseModal] = useState<UnableToDispenseModalState>({
    visible: false,
    data: null,
  });
  const [confirmDispenseModal, setConfirmDispenseModal] = useState<ConfirmDispenseModalState>({
    visible: false,
    orderIds: [],
  });
  const [markAsCollectedModal, setMarkAsCollectedModal] = useState<MarkAsCollectedModalState>({
    visible: false,
    orderIds: [],
  });
  const [splitOrderModal, setSplitOrderModal] = useState<SplitOrderModalState>({ visible: false, orderDetails: null });

  const value = useMemo(
    () => ({
      approvalDocumentsModal,
      unableToDispenseModal,
      confirmDispenseModal,
      markAsCollectedModal,
      splitOrderModal,
      setApprovalDocumentsModal,
      setUnableToDispenseModal,
      setConfirmDispenseModal,
      setMarkAsCollectedModal,
      setSplitOrderModal,
    }),
    [approvalDocumentsModal, unableToDispenseModal, confirmDispenseModal, markAsCollectedModal, splitOrderModal],
  );

  return <PharmacistOrdersModalContext.Provider value={value}>{children}</PharmacistOrdersModalContext.Provider>;
}

export const usePharmacistApprovalDocumentsModal = () => {
  const { approvalDocumentsModal, setApprovalDocumentsModal } = useContext(PharmacistOrdersModalContext);

  const showApprovalDocumentsModal = (patientCode: string, orderId: number) => {
    setApprovalDocumentsModal({
      visible: true,
      patientCode,
      orderId,
    });
  };

  const hideApprovalDocumentsModal = () => {
    setApprovalDocumentsModal({
      visible: false,
      patientCode: null,
      orderId: null,
    });
  };

  return {
    approvalDocumentsModal,
    showApprovalDocumentsModal,
    hideApprovalDocumentsModal,
  };
};

export const usePharmacistConfirmDispenseModal = () => {
  const { confirmDispenseModal, setConfirmDispenseModal } = useContext(PharmacistOrdersModalContext);
  const { enqueueError } = useErrorManagement({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showConfirmDispenseModal = useCallback(
    async (selectedOrders: number[], refetchOrdersFunction: () => void) => {
      if (selectedOrders.length === 0) {
        toast.info('Select orders using checkboxes before proceeding with action.');
      } else {
        setConfirmDispenseModal({
          visible: true,
          orderIds: selectedOrders,
          refetchOrdersFunction: refetchOrdersFunction,
        });
      }
    },
    [setConfirmDispenseModal],
  );

  const hideConfirmDispenseModal = () => {
    setConfirmDispenseModal({
      visible: false,
      orderIds: [],
      refetchOrdersFunction: undefined,
    });
  };

  const confirmDispense = useCallback(async () => {
    try {
      setIsSubmitting(true);
      const response = await confirmDispenseOrder(confirmDispenseModal.orderIds);
      const { data } = response;

      if (!data) {
        toast.error('Something went wrong');
        logger.error('Failed to confirm dispense', data);
      }

      if (!Array.isArray(data.data) && Array.isArray(data.data.successes) && Array.isArray(data.data.errors)) {
        if (data.data.errors?.length > 0 || data.data.successes?.length === 0) {
          enqueueError({
            persist: true,
            title: 'Some of your orders did not go through',
            body: (
              <>
                Expect an email with the failed Order IDs and please get in touch with our support team at{' '}
                <b>pharmacy-ops@montu.com.au</b> for a quick resolution.
              </>
            ),
          });
        }
        if (data.data.successes.length > 0) {
          toast.success(`${data.data.successes.length} orders have been dispensed`);
        }
        //   legacy path
      } else if (Array.isArray(data.data)) {
        const hasErrors = data.data?.some((orderResult) => !orderResult.success);
        if (hasErrors) {
          enqueueError({
            persist: true,
            title: 'Some of your orders did not go through',
            body: (
              <>
                Expect an email with the failed Order IDs and please get in touch with our support team at{' '}
                <b>pharmacy-ops@montu.com.au</b> for a quick resolution.
              </>
            ),
          });
          toast.success(`${data.data.length} orders have been dispensed`);
        }
      } else if (data && 'message' in data && typeof data.message === 'string') {
        // it's the error object variant
        toast.warning(data.message);
      } else {
        toast.warning('Something went wrong');
        logger.error('Failed to confirm dispense', data);
      }
    } catch (error: any) {
      toast.error(`One or more orders have failed to be dispensed`);
      logger.error(`One or more orders have failed to be dispensed`, error);
    } finally {
      confirmDispenseModal.refetchOrdersFunction?.();
      hideConfirmDispenseModal();
      setIsSubmitting(false);
    }
  }, [confirmDispenseModal, enqueueError, hideConfirmDispenseModal]);

  return { confirmDispense, confirmDispenseModal, showConfirmDispenseModal, hideConfirmDispenseModal, isSubmitting };
};

export const usePharmacistUnableToDispenseModal = () => {
  const { unableToDispenseModal, setUnableToDispenseModal } = useContext(PharmacistOrdersModalContext);

  const showUnableToDispenseModal = async (orderId: number) => {
    try {
      const data = await getUnableToDispenseInfo(orderId);
      setUnableToDispenseModal({
        visible: true,
        data,
      });
    } catch (error: any) {
      logger.error(error.message || 'Failed to retrieve information');
      toast.error(error.message || 'Failed to retrieve information');
    }
  };

  const hideUnableToDispenseModal = () => {
    setUnableToDispenseModal({
      visible: false,
      data: null,
    });
  };

  return { showUnableToDispenseModal, unableToDispenseModal, hideUnableToDispenseModal };
};

type OrderFromListOrders = ArrayElement<ListOrdersResponse[keyof ListOrdersResponse]>;

export const usePharmacistSplitOrderModal = () => {
  const { splitOrderModal, setSplitOrderModal } = useContext(PharmacistOrdersModalContext);
  const { unableToDispenseReasons, refetchOrders } = useContext(PharmacistOrdersContext);

  const showSplitOrderModal = (orderDetails: PharmacistOrder | OrderFromListOrders) => {
    let details: SplitOrderModalOrderData;
    if ('order_code' in orderDetails) {
      details = {
        orderId: orderDetails.orderId,
        orderCode: orderDetails.order_code ?? '',
        shippedDate: orderDetails.shipped_date,
        patientCode: orderDetails.patient_id,
        prescriptionNames: orderDetails.prescriptions.map(({ name }) => name),
        productDetails: orderDetails.product_details.map((productDetails, index) => ({
          name: productDetails.name,
          id: productDetails.id,
          quantity: orderDetails.quantity[index] || undefined,
        })),
      };
    } else {
      // listOrders path (one of the newer delivery tables)
      details = {
        orderId: orderDetails.orderId,
        orderCode: orderDetails.orderCode,
        shippedDate: orderDetails.shippedDate,
        patientCode: orderDetails.patient.patientCode,
        prescriptionNames: orderDetails.prescriptions.map(({ code }) => code),
        productDetails: orderDetails.orderProducts.map(({ product, quantity }) => ({
          name: product.name,
          id: product.id,
          quantity,
        })),
      };
    }
    setSplitOrderModal({
      visible: true,
      orderDetails: details,
    });
  };

  const hideSplitOrderModal = () => {
    setSplitOrderModal({
      visible: false,
      orderDetails: null,
    });
  };

  const saveUnableToDispense = async (
    productsWithReasons: UnableToDispenseProduct[],
  ): Promise<SaveUnableToDispenseResult> => {
    if (
      productsWithReasons.some(
        ({ reason, otherReason }) => reason?.label === UnableToDispenseReasonLabel.Others && !otherReason,
      )
    ) {
      return {
        success: false,
        message: 'Please provide the issue',
      };
    }
    try {
      const response = await unableToDispense(productsWithReasons);
      if (response.status === 200) {
        refetchOrders();
      }
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'An error occurred while updating the order',
      };
    }
    return { success: true };
  };

  return { showSplitOrderModal, unableToDispenseReasons, splitOrderModal, hideSplitOrderModal, saveUnableToDispense };
};

const client = getTsPharmacyClient();

export const useMarkAsCollectedModal = () => {
  const queryClient = useQueryClient();
  const clearSelectedOrders = useClearSelectedOrders();
  const { markAsCollectedModal, setMarkAsCollectedModal } = useContext(PharmacistOrdersModalContext);
  const markOrdersAsCollectedMutation = client.markOrdersAsCollected.useMutation({
    meta: {
      error: {
        showToast: true,
        message: 'Something went wrong',
        log: 'markOrdersAsCollected failed',
      },
    },
    onSuccess(_data, variables) {
      const count = variables.body.data.orderIds.length;
      toast.success(`Order${count > 1 ? 's' : ''} successfully marked as collected`);
      logger.info('orders marked as collected', {
        orderIds: variables.body.data.orderIds,
        count,
      });
      clearSelectedOrders();
      queryClient.invalidateQueries({ queryKey: client.listOrders.keys });
    },
  });

  const showMarkAsCollectedModal = async ({ orderIds }: { orderIds: number[] }) => {
    if (orderIds.length === 0) {
      toast.info('Select orders using checkboxes before proceeding with action.');
    } else {
      setMarkAsCollectedModal({
        visible: true,
        orderIds,
      });
    }
  };

  const hideMarkAsCollectedModal = () => {
    setMarkAsCollectedModal({
      visible: false,
      orderIds: undefined,
    });
  };

  return { showMarkAsCollectedModal, markAsCollectedModal, hideMarkAsCollectedModal, markOrdersAsCollectedMutation };
};
